import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { isLiveDraft, pickMostRecentDraft } from '@/lib/crm/live-draft';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ThreadMessage {
  id: string;
  incoming_message: string;
  incoming_at: string;
  draft_response: string;
  edited_response: string | null;
  methodology_used: string | null;
  confidence: number;
  status: 'pending_approval' | 'approved' | 'sent' | 'rejected' | 'failed' | 'awaiting_draft';
  is_superseded: boolean;
  sent_at: string | null;
  created_at: string;
  detected_language: string | null;
  analysis_report: Record<string, unknown> | null;
  strategy: Record<string, unknown> | null;
  alternatives: Array<Record<string, unknown>> | null;
  decision_trace: Record<string, unknown> | null;
  selected_alternative: number | null;
  human_edit_diff: string | null;
  draft_versions: Array<{
    text: string;
    methodology: string;
    confidence: number;
    approach?: string;
    generated_at: string;
    instruction?: string;
  }> | null;
  regenerated_at: string | null;
  regeneration_count: number | null;
  constraint_validation: {
    passed: boolean;
    score: number;
    total: number;
    constraints: Array<{
      id: string;
      rule: string;
      passed: boolean;
      detail?: string;
    }>;
  } | null;
  heyreach_lead_id: string;
  heyreach_campaign_id: string;
  heyreach_sender_id: string | null;
  lead_name: string;
  lead_title: string | null;
  lead_company: string | null;
  lead_linkedin_url: string | null;
  organization_id: string;
  user_id: string | null;
  contact_id: string | null;
  sender_id: string | null;
  thread_id: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const THREAD_MESSAGE_COLUMNS = `
  id,
  incoming_message,
  incoming_at,
  draft_response,
  edited_response,
  methodology_used,
  confidence,
  status,
  is_superseded,
  sent_at,
  created_at,
  detected_language,
  analysis_report,
  strategy,
  alternatives,
  decision_trace,
  selected_alternative,
  human_edit_diff,
  draft_versions,
  regenerated_at,
  regeneration_count,
  constraint_validation,
  heyreach_lead_id,
  heyreach_campaign_id,
  heyreach_sender_id,
  lead_name,
  lead_title,
  lead_company,
  lead_linkedin_url,
  organization_id,
  user_id,
  contact_id,
  sender_id,
  thread_id
` as const;

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export const useThreadMessages = (threadId: string | undefined): ReturnType<typeof useQuery<ThreadMessage[]>> => {
  return useQuery<ThreadMessage[]>({
    queryKey: ['thread-messages', threadId],
    queryFn: async () => {
      if (!threadId) return [];

      const { data, error } = await supabase
        .from('os_linkedin_drafts')
        .select(THREAD_MESSAGE_COLUMNS)
        .eq('thread_id', threadId)
        .order('incoming_at', { ascending: true });

      if (error) throw error;
      return (data || []) as ThreadMessage[];
    },
    enabled: !!threadId,
    staleTime: 10000,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The ONE live-draft winner for a thread — selected ONCE across the
 * COMBINED `awaiting_draft | pending_approval` vocabulary (`isLiveDraft`
 * already unions both statuses; not superseded, non-empty message), via the
 * SAME shared comparator the Reply Queue and Lead Dossier use
 * (`pickMostRecentDraft`, src/lib/crm/live-draft.ts).
 *
 * F3 (round 12) fixed each status's own tiebreak/ordering but still let the
 * page run TWO independent selections and give `pending_approval` absolute
 * precedence over `awaiting_draft` (`!activeDraft && awaitingDraft`) — a
 * reachable generate/webhook race: draft A enters generation, a newer
 * inbound B arrives as `awaiting_draft` (the webhook supersedes only
 * already-`pending_approval` rows, so it does NOT touch still-generating
 * A), A completes and unconditionally becomes `pending_approval` with no
 * compare-and-set against a newer inbound (api/os/linkedin/
 * generate-draft.ts). Two-selector-plus-priority then opened OLDER A while
 * Queue/Dossier (which pick the newest live draft across BOTH statuses in
 * one comparison) pointed at NEWER B. Callers must derive `activeDraft`/
 * `awaitingDraft` as VIEWS of this single winner's status — never re-pick.
 */
export const getLiveDraftWinner = (messages: ThreadMessage[]): ThreadMessage | null => {
  return pickMostRecentDraft(messages.filter(isLiveDraft));
};
